import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

// The data domain is South African schools rugby. Treat user-entered datetimes
// as SAST (Africa/Johannesburg) regardless of the browser's actual timezone.
export const DISPLAY_TZ = 'Africa/Johannesburg';

const HAS_TZ_SUFFIX = /Z$|[+-]\d{2}:?\d{2}$/;

// Parse an API datetime string defensively: if it has a timezone suffix
// (`Z` or `±HH:MM`), use it; otherwise treat as UTC. Older API endpoints
// sometimes return naive datetimes that are implicitly UTC.
export function parseApiDateTime(apiDateTimeStr) {
  if (!apiDateTimeStr) return null;
  const d = HAS_TZ_SUFFIX.test(apiDateTimeStr)
    ? dayjs(apiDateTimeStr)
    : dayjs.utc(apiDateTimeStr);
  return d.isValid() ? d.tz(DISPLAY_TZ) : null;
}

// Convert a naive local datetime (e.g. from a <input type="datetime-local">)
// to a UTC ISO 8601 string with explicit `Z` suffix, ready for the API.
// If the input already carries a timezone marker, it's an unmodified
// round-trip from the API and is treated as authoritative. Otherwise the
// input is interpreted as SAST.
export function localInputToApiUtc(localDateTimeStr) {
  if (!localDateTimeStr) return null;
  const hasTimezone = HAS_TZ_SUFFIX.test(localDateTimeStr);
  try {
    const d = hasTimezone ? dayjs(localDateTimeStr) : dayjs.tz(localDateTimeStr, DISPLAY_TZ);
    return d.isValid() ? d.utc().format('YYYY-MM-DDTHH:mm:ss[Z]') : null;
  } catch (e) {
    return null;
  }
}

// Display an API datetime in SA local time, e.g. "28 Apr 2026, 14:30".
export function formatDateTimeForDisplay(apiDateTimeStr) {
  const d = parseApiDateTime(apiDateTimeStr);
  return d ? d.format('DD MMM YYYY, HH:mm') : 'N/A';
}

// Display only the date portion in SA local time, e.g. "28 Apr 2026".
export function formatDateForDisplay(apiDateTimeStr) {
  const d = parseApiDateTime(apiDateTimeStr);
  return d ? d.format('DD MMM YYYY') : 'N/A';
}

// Format an API datetime for a `<input type="datetime-local">` field
// (which expects naive local time as `YYYY-MM-DDTHH:mm`).
export function apiUtcToDatetimeLocalInput(apiDateTimeStr) {
  const d = parseApiDateTime(apiDateTimeStr);
  return d ? d.format('YYYY-MM-DDTHH:mm') : '';
}
